import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useRef } from 'react';
import { T } from 'src/shared/helpers';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import styled from 'styled-components';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { getComposerActionDefinition } from '../../../configurator-components/composer-editors/attributes/composerActionCatalog';
import { ComposerOptionActionNode, ComposerOptionActionsType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { setComposerRowDragImage } from '../helpers/composerDragImage';

interface ComposerActionTreeElementProps {
    action: ComposerOptionActionNode;
    findMesh: (meshId: string) => BABYLON.AbstractMesh | undefined;
    findMaterial: (materialId: string) => BABYLON.Material | undefined;
    findCamera: (cameraId: string) => BABYLON.Camera | undefined;
    findPrintingMethod?: (printingMethodId: string) => { name?: string } | undefined;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onPlay?: (action: ComposerOptionActionNode) => void;
    onRemove?: (action: ComposerOptionActionNode) => void;
    onVisibilityClick?: (action: ComposerOptionActionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onMoveUp?: (action: ComposerOptionActionNode) => void;
    onMoveDown?: (action: ComposerOptionActionNode) => void;
    onDragStart?: (event: React.DragEvent<HTMLButtonElement>, action: ComposerOptionActionNode) => void;
    onDragEnd?: (event: React.DragEvent<HTMLButtonElement>, action: ComposerOptionActionNode) => void;
    onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
    selected: boolean;
    editor?: React.ReactNode;
}

const Row = styled.div<{ $selected: boolean; $muted: boolean }>`
    position: relative;
    display: grid;
    min-width: 0;
    min-height: 30px;
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 0.9fr) auto;
    align-items: start;
    gap: 6px;
    padding: 6px;
    color: var(--composer-fg, #1e2a38);
    background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'transparent')};
    border: 0;
    border-radius: 5px;
    cursor: pointer;
    outline: none;
    user-select: none;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'var(--composer-accent-wash, #fdf0e6)' : 'var(--composer-hover, #e9eff5)')};
    }

    ${(props) => props.$selected && 'box-shadow: inset 2px 0 0 var(--composer-accent, #e8650a);'}

    /* A disabled action reads as spent, its own icon apart. */
    > div:not(:last-child) {
        opacity: ${(props) => (props.$muted ? 0.5 : 1)};
    }

    /* Row commands appear on hover, except the ones marked persistent. :focus-within alone would keep them lit
       after a plain click, since the row itself takes focus. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }
`;

const Cell = styled.div<{ $title?: boolean }>`
    min-width: 0;
    overflow-wrap: anywhere;
    font-size: 12px;
    font-weight: ${(props) => (props.$title ? 600 : 400)};
    line-height: 1.35;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 1px;
`;

const UNDEFINED_PLACEHOLDER = '—';

export const ComposerActionTreeElement: FC<ComposerActionTreeElementProps> = ({
    action,
    onSelect,
    onDoubleClick,
    onPlay,
    onRemove,
    onVisibilityClick,
    findMesh,
    findMaterial,
    findCamera,
    findPrintingMethod,
    selected,
    onMoveUp,
    onMoveDown,
    onDragStart,
    onDragEnd,
    onDrop,
    onDragOver,
    editor
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const enabled = action.enabled !== false;
    const aggregate = (find: (id: string) => { name?: string } | undefined, value: string) =>
        value
            .split(/[;,]/)
            .filter(Boolean)
            .map((id) => find(id.trim())?.name ?? id.trim())
            .join(', ');

    const [selector, value] = React.useMemo(() => {
        switch (action.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
                return [aggregate(findMesh, action.selector), ''];
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return [aggregate(findMesh, action.selector), ''];
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return [aggregate(findMesh, action.selector), aggregate(findMaterial, action.value)];
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return [aggregate(findMaterial, action.selector), action.value];
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return [aggregate(findMaterial, action.selector), `${action.property ?? ''}: ${action.value}`];
            case ComposerOptionActionsType.MOVE_CAMERA:
                return [UNDEFINED_PLACEHOLDER, aggregate(findCamera, action.selector || action.value)];
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return [aggregate(findMesh, action.selector), aggregate(findMaterial, action.value)];
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, aggregate(findCamera, action.value)];
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return [UNDEFINED_PLACEHOLDER, findPrintingMethod?.(action.value)?.name || action.value];
            default:
                return [action.selector, action.value];
        }
    }, [action, findCamera, findMaterial, findMesh, findPrintingMethod]);

    const name = getComposerActionDefinition(action.type).name;

    return (
        <>
            <Row
                ref={rowRef}
                $selected={selected}
                $muted={!enabled}
                aria-selected={selected}
                onClick={(event) => onSelect(event, action)}
                onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, action) : undefined}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' && event.altKey) {
                        event.preventDefault();
                        onMoveUp?.(action);
                    }
                    if (event.key === 'ArrowDown' && event.altKey) {
                        event.preventDefault();
                        onMoveDown?.(action);
                    }
                }}
                role='row'
                tabIndex={0}
            >
                <Cell $title title={name} role='cell'>
                    {name}
                </Cell>
                <Cell title={selector || UNDEFINED_PLACEHOLDER} role='cell'>
                    {selector || UNDEFINED_PLACEHOLDER}
                </Cell>
                <Cell title={value || UNDEFINED_PLACEHOLDER} role='cell'>
                    {value || UNDEFINED_PLACEHOLDER}
                </Cell>
                <Actions>
                    <ZTooltip title={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}>
                        <ActionButton
                            $off={!enabled}
                            aria-label={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}
                            aria-pressed={!enabled}
                            data-persistent={enabled ? undefined : 'true'}
                            onClick={(event) => {
                                event.stopPropagation();
                                onVisibilityClick?.(action);
                            }}
                        >
                            {enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </ActionButton>
                    </ZTooltip>
                    <ZTooltip title={T._('Run this action', 'Admin')}>
                        <ActionButton
                            aria-label={T._('Run this action', 'Admin')}
                            onClick={(event) => {
                                event.stopPropagation();
                                onPlay?.(action);
                            }}
                        >
                            <PlayIcon />
                        </ActionButton>
                    </ZTooltip>
                    <ZTooltip title={T._('Remove action', 'Admin')}>
                        <ActionButton
                            $danger
                            aria-label={T._('Remove action', 'Admin')}
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove?.(action);
                            }}
                        >
                            <TrashIcon />
                        </ActionButton>
                    </ZTooltip>
                    <ZTooltip title={T._('Drag to reorder · Alt+Arrow to move', 'Admin')}>
                        <ActionButton
                            aria-label={T._('Drag to reorder', 'Admin')}
                            draggable={!!onDragStart}
                            onDragEnd={(event) => onDragEnd?.(event, action)}
                            onDragStart={(event) => {
                                event.stopPropagation();
                                setComposerRowDragImage(event.dataTransfer, rowRef.current);
                                onDragStart?.(event, action);
                            }}
                        >
                            <DragIcon />
                        </ActionButton>
                    </ZTooltip>
                </Actions>
            </Row>
            {editor}
        </>
    );
};
